import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Building2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const empty = { name: "", service_type_id: "", office_number: "", zip_code: "", address: "", phone: "", manager_name: "", status: "運営中" };

export default function Offices() {
  const [offices, setOffices] = useState(null);
  const [svc, setSvc] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/service-types").then(({ data }) => setSvc(data)); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, service_type_id: svc[0]?.id || "" }); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm(o); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.service_type_id) { toast.error("事業所名とサービス種別は必須です"); return; }
    try {
      if (editing) await api.put(`/offices/${editing.id}`, form);
      else await api.post("/offices", form);
      toast.success("保存しました"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!offices) return <Loading />;

  return (
    <div>
      <PageHeader title="事業所管理" subtitle={`${offices.length}事業所`}
        actions={<Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="add-office-btn"><Plus className="w-4 h-4 mr-1" /> 事業所を追加</Button>} />
      {offices.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {offices.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`office-${o.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="p-2.5 rounded-lg bg-blue-50"><Building2 className="w-5 h-5 text-blue-600" /></div>
                <button onClick={() => openEdit(o)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-office-${o.id}`}><Pencil className="w-4 h-4" /></button>
              </div>
              <div className="font-medium text-slate-800">{o.name}</div>
              <div className="text-xs text-slate-500 mt-1">{o.service_type_name}</div>
              <div className="text-xs text-slate-400 mt-2">事業所番号：{o.office_number || "-"}</div>
              <div className="text-xs text-slate-400">管理者：{o.manager_name || "-"}</div>
              <div className="mt-3"><StatusBadge status={o.status} /></div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="事業所がありません" />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "事業所を編集" : "事業所を追加"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>事業所名 *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="office-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>サービス種別 *</Label>
                <Select value={form.service_type_id} onValueChange={(v) => setForm({ ...form, service_type_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="office-svc-select"><SelectValue placeholder="選択" /></SelectTrigger>
                  <SelectContent>{svc.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>事業所番号</Label><Input value={form.office_number || ""} onChange={(e) => setForm({ ...form, office_number: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>住所</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>電話番号</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              <div><Label>管理者</Label><Input value={form.manager_name || ""} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>利用状態</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="運営中">運営中</SelectItem><SelectItem value="休止">休止</SelectItem><SelectItem value="利用停止">利用停止</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-office-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
